import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Drawer from '@material-ui/core/Drawer';
import Typography from '@material-ui/core/Typography';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { removeLegacyDraftById } from 'features/drafts';
import { FORM_FIELDS_MAP, FROM_SOURCE_KEY, SOURCES } from 'features/new-candidate/constants';
import { useShowAlert } from 'hooks';
import useUnload from 'hooks/unload';
import useScrollToError from 'hooks/useErrorScroll';
import LogRocket from 'logrocket';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import strings from 'strings';
import { FPActionButton, SaveButton, When } from 'UI/components/atoms';
import {
  formatPhoneNumbers,
  PHONE_FORMAT_MODES
} from 'UI/components/organisms/PhoneNumbersForm/utils';
import { Endpoints } from 'UI/constants/endpoints';
import { ContactRole, EntityType } from 'UI/constants/entityTypes';
import { LogRocketCustomEvents } from 'UI/constants/LogRocketCustomEvents';
import { UIStatus } from 'UI/constants/status';
import { CandidateDataSheetDrawer } from 'UI/pages/NewCandidates/CandidateDataSheetDrawer';
import { REQUIRED_VALIDATION } from 'UI/utils';
import { isValidEmail, prepareFormToSubmit } from 'UI/utils/forms';

import {
  useAttachments,
  useCandidateDraftAutosave,
  useDataSheet,
  useRegisteredEmails
} from './NewCandidateForm.hooks';
import { AttachmentsSection, DataSheetSection, MainCandidateSection, RecruiterSection } from './ui';

export const NewCandidateForm = ({
  formattedResults,
  hasSuperiorRole,
  isUserCoach,
  onRegisterLeaveGuard = () => {},
  preselectedCompany
}) => {
  const history = useHistory();
  const { showAlert } = useShowAlert();

  const [uiStatus, setUiStatus] = useState(UIStatus.Default);
  const isSubmittedRef = useRef(false);
  const defaultValues = {
    ...(formattedResults && formattedResults),
    ...(preselectedCompany && { [FORM_FIELDS_MAP.Company.key]: preselectedCompany }),
    ...(isUserCoach && { [FORM_FIELDS_MAP.Recruiter.key]: null })
  };

  const methods = useForm({ defaultValues });
  const { errors, getValues, register, setError, setValue, unregister, watch } = methods;
  useScrollToError({ errors });

  const watchers = watch([
    FORM_FIELDS_MAP.DataSheet.key,
    FORM_FIELDS_MAP.Email.key,
    FORM_FIELDS_MAP.FirstName.key,
    FORM_FIELDS_MAP.LastName.key,
    FORM_FIELDS_MAP.PersonalEmail.key,
    FORM_FIELDS_MAP.Recruiter.key,
    FORM_FIELDS_MAP.FromContact.key,
    FROM_SOURCE_KEY
  ]);
  const { dataSheet, email, firstName, lastName, recruiter, personalEmail, fromSource } = watchers;
  const dataSheetId = dataSheet?.id;

  /* --- EMAIL VALIDATIONS --- */
  const isFromDirectory =
    fromSource === SOURCES.FROM_DIRECTORY || Boolean(formattedResults?.fromContact?.id);
  const isFromTalentPool =
    fromSource === SOURCES.FROM_TALENT_POOL || Boolean(formattedResults?.applicantId);

  const emailsValidation = useRegisteredEmails({
    email,
    isFromDirectory,
    isFromTalentPool,
    otherEmail: personalEmail
  });

  /* --- CONSTANTS --- */
  const isRegistered = emailsValidation.isValidating || emailsValidation.isRegistered;
  const canSaveDraft =
    isValidEmail(email) && (firstName ?? '').trim() && (lastName ?? '').trim() && !isRegistered;
  const canSubmit = hasSuperiorRole || !isRegistered;
  const legacyDraftId = formattedResults?.legacyDraftId;

  /* --- DATA SHEET --- */
  const {
    closeDataSheet,
    handleSaveCandidateDataSheet: onSaveDataSheet,
    isDataSheetOpen,
    openDataSheet
  } = useDataSheet({
    setValue
  });

  /* --- ATTACHMENTS --- */
  const { attachments, handleAttachmentsChanged: onAttachmentsChanged } = useAttachments({
    initialAttachments: formattedResults?.attachments ?? []
  });
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  /* --- DRAFTS / AUTOSAVE --- */
  const {
    cancelAutosave,
    deleteDraftById,
    draftId,
    draftUpdatedAt,
    flushAutosave,
    hasUnsavedChanges,
    isSavingDraft,
    lastSavedSnapshotRef,
    saveDraft,
    triggerAutosave,
    updateDraft
  } = useCandidateDraftAutosave({
    attachments,
    canSaveDraft,
    defaultValues,
    formattedResults,
    getFormValues: getValues,
    preselectedCompany
  });
  const draftTextButton = draftId ? strings.drafts.button.update : strings.drafts.button.save;

  const hasUnsavedNewCandidateData = useCallback(() => {
    if (draftId || legacyDraftId) return false;
    if (lastSavedSnapshotRef.current) return false;
    if (canSaveDraft) return false;
    const values = getValues();
    const hasIdentity = Boolean(
      (values[FORM_FIELDS_MAP.Email.key] || '').trim() ||
        (values[FORM_FIELDS_MAP.FirstName.key] || '').trim() ||
        (values[FORM_FIELDS_MAP.LastName.key] || '').trim()
    );
    const hasAttachments = (attachmentsRef.current ?? []).length > 0;
    return hasIdentity || hasAttachments;
  }, [draftId, legacyDraftId, canSaveDraft, getValues, lastSavedSnapshotRef]);

  const shouldWarnOnLeave = useCallback(
    () => !isRegistered && (hasUnsavedChanges() || hasUnsavedNewCandidateData()),
    [isRegistered, hasUnsavedChanges, hasUnsavedNewCandidateData]
  );

  useUnload(e => {
    if (shouldWarnOnLeave()) {
      e.preventDefault();
      e.returnValue = '';
      flushAutosave();
    }
  });

  useEffect(() => {
    onRegisterLeaveGuard(() => {
      if (isSubmittedRef.current) return true;
      if (shouldWarnOnLeave()) {
        return strings.candidates.creation.leaveForm.alertBody;
      }
      return true;
    });

    return () => {
      onRegisterLeaveGuard();
    };
  }, [shouldWarnOnLeave, onRegisterLeaveGuard]);

  const handleSaveDraft = () => {
    cancelAutosave();
    saveDraft({ ...getValues(), attachments });
    LogRocket.track(LogRocketCustomEvents.NewCandidate.SaveDraft);
  };

  /* --- RHF REGISTER --- */
  useEffect(() => {
    register({ name: FROM_SOURCE_KEY });
    register({ name: FORM_FIELDS_MAP.FromContact.key });
    register({ name: FORM_FIELDS_MAP.DataSheet.key }, REQUIRED_VALIDATION);

    isUserCoach &&
      register({
        name: FORM_FIELDS_MAP.Recruiter.key
      });

    return () => {
      unregister(FROM_SOURCE_KEY);
      unregister(FORM_FIELDS_MAP.FromContact.key);
      unregister(FORM_FIELDS_MAP.DataSheet.key);
      if (isUserCoach) unregister(FORM_FIELDS_MAP.Recruiter.key);
    };
  }, [isUserCoach, register, unregister]);

  /* --- HANDLERS --- */
  const triggerAutosaveAfterUserChange = useCallback(
    (formValues, currentAttachments) => {
      triggerAutosave(formValues, currentAttachments);
    },
    [triggerAutosave]
  );

  const handleValuesChanged = useCallback(
    (formValues, options = {}) => {
      updateDraft(formValues);
      if (options.isUserChange) {
        triggerAutosaveAfterUserChange(formValues, attachmentsRef.current);
      }
    },
    [updateDraft, triggerAutosaveAfterUserChange]
  );

  const handleValueChange = (name, value) => {
    setValue(name, value, true);
    triggerAutosaveAfterUserChange(getValues(), attachmentsRef.current);
  };

  const handleAttachmentsChanged = useCallback(
    data => {
      const { attachments: nextAttachments, hasChanged } = onAttachmentsChanged(data);
      if (hasChanged) {
        triggerAutosaveAfterUserChange(getValues(), nextAttachments);
      }
    },
    [onAttachmentsChanged, triggerAutosaveAfterUserChange, getValues]
  );

  const handleSaveCandidateDataSheet = useCallback(
    (candidateDataSheetID, progress) => {
      onSaveDataSheet(candidateDataSheetID, progress);
      triggerAutosaveAfterUserChange(getValues(), attachmentsRef.current);
    },
    [onSaveDataSheet, triggerAutosaveAfterUserChange, getValues]
  );

  /* --- SUBMIT HANDLER --- */
  const submitNewCandidate = useCallback(
    async (formData, persistedDraftId) => {
      const answered = formData?.dataSheet?.progress?.answered;
      const total = formData?.dataSheet?.progress?.total;
      const isProgressIncomplete =
        !Number.isFinite(answered) || !Number.isFinite(total) || answered < total;

      if (isProgressIncomplete) {
        setError(
          FORM_FIELDS_MAP.DataSheet.key,
          'manual',
          strings.candidates.editDataSheet.uiMessages.warning
        );
        return;
      }

      try {
        setUiStatus(UIStatus.Saving);

        const preparedCandidate = prepareFormToSubmit(formData, FORM_FIELDS_MAP);
        const preparePhoneNumbers = formatPhoneNumbers(formData, PHONE_FORMAT_MODES.submit);

        const candidateData = {
          ...preparedCandidate,
          dataSheetId: formData?.dataSheet?.id,
          files: attachments.map(att => att.id),
          phones: preparePhoneNumbers
        };

        const fromContactId = formData[FORM_FIELDS_MAP.FromContact.key]?.id;
        const isFromContact =
          formData[FORM_FIELDS_MAP.FromContact.key]?.role_id === ContactRole.Name;
        isFromContact && (candidateData.hiringId = null);

        const endpoint = isFromContact
          ? `${Endpoints.Candidates}/fromName/:id`.replace(':id', fromContactId)
          : `${Endpoints.Candidates}`;

        const response = await API.post(endpoint, candidateData);

        if (response.data && response.status !== HTTPStatusCodes.Created) {
          setUiStatus(UIStatus.Error);
          showAlert({
            autoHideDuration: 8000,
            error: response,
            severity: 'error',
            title: strings.candidates.creation.title
          });
          return;
        }

        const draftIdToDelete = persistedDraftId || draftId;

        if (draftIdToDelete) {
          await deleteDraftById(draftIdToDelete);
        }
        if (legacyDraftId) {
          removeLegacyDraftById({
            draftId: legacyDraftId,
            entity: EntityType.Candidate
          });
        }

        showAlert({
          severity: 'success',
          title: strings.candidates.creation.success.title,
          body: strings.candidates.creation.success.body
        });
        isSubmittedRef.current = true;
        LogRocket.track(LogRocketCustomEvents.NewCandidate.Save);
        history.push(EntityRoutes.CandidateProfile.replace(':id', response.data.data.id));
      } catch (error) {
        setUiStatus(UIStatus.Error);

        showAlert({
          severity: 'error',
          title: 'Candidates',
          autoHideDuration: 5000,
          error
        });
        LogRocket.captureException(error, {
          extra: {
            event: LogRocketCustomEvents.NewCandidate.Save
          }
        });
      }
    },
    [attachments, deleteDraftById, draftId, history, legacyDraftId, setError, showAlert]
  );

  const handleFormSubmit = useCallback(
    async formData => {
      const persistedDraftId = await flushAutosave();

      await submitNewCandidate(formData, persistedDraftId);
    },
    [flushAutosave, submitNewCandidate]
  );

  /* --- RENDERS --- */
  return (
    <>
      <FormContext {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleFormSubmit)}
          onChange={() => triggerAutosaveAfterUserChange(getValues(), attachmentsRef.current)}
        >
          <MainCandidateSection
            draftId={draftId}
            emailsValidation={emailsValidation}
            hasSuperiorRole={hasSuperiorRole}
            updateDraft={handleValuesChanged}
          />

          <DataSheetSection
            disabled={!canSaveDraft}
            dataSheetId={dataSheetId}
            onClick={openDataSheet}
          />

          <AttachmentsSection
            initialFiles={attachments}
            onAttachmentsChanged={handleAttachmentsChanged}
          />

          <When condition={isUserCoach}>
            <RecruiterSection onSelect={handleValueChange} recruiter={recruiter} />
          </When>

          <Box display="flex" justifyContent="flex-end" alignItems="center" gridGap={16}>
            <When condition={!!draftUpdatedAt}>
              <Typography variant="body2">
                {strings.formatString(strings.drafts.lastSaved, {
                  updatedAt: draftUpdatedAt
                })}
              </Typography>
            </When>

            <When condition={emailsValidation.isValidating}>
              <CircularProgress size={30} />
            </When>

            <FPActionButton
              disabled={!canSaveDraft || isSavingDraft}
              text={isSavingDraft ? `${strings.shared.ui.saving} draft...` : draftTextButton}
              variant="outlined"
              onClick={handleSaveDraft}
              startIcon={isSavingDraft && <CircularProgress size={20} />}
            />

            <SaveButton disabled={!canSubmit} isSaving={uiStatus === UIStatus.Saving} />
          </Box>
        </form>
      </FormContext>
      <When condition={isDataSheetOpen}>
        <CandidateDataSheetDrawer
          candidateDataSheetId={dataSheetId}
          drawerTitle={strings.candidates.create.candidateDataSheet.new.title}
          onClose={closeDataSheet}
          onSaveCandidateDataSheet={handleSaveCandidateDataSheet}
        />
      </When>
    </>
  );
};
